import { request } from "@/api/client"

const BASE_PATH = "/venues/ingestion"

export type VenueCandidateRecord = {
  id: string
  artist_id: string
  source_type: string
  venue_name: string
  city?: string
  state?: string
  country?: string
  website?: string
  booking_email?: string
  phone?: string
  address?: string
  capacity?: number
  genres: string[]
  notes?: string
  status: string
  confidence?: number
  confidence_score?: number
  confidence_reasons?: Record<string, unknown>
  source_data?: Record<string, unknown>
  enrichment_data?: Record<string, unknown>
  enrichment_source?: string
  enrichment_payload?: Record<string, unknown>
  reviewed_by?: string
  reviewed_at?: string
  rejection_reason?: string
  created_at?: string
  updated_at?: string
}

export type FetchVenueCandidatesParams = {
  status?: "pending" | "approved" | "rejected" | "all"
  limit?: number
}

export type CandidateApprovePayload = {
  venue_name?: string
  city?: string
  state?: string
  country?: string
  website?: string
  booking_email?: string
  phone?: string
  notes?: string
  confidence?: number
}

export type CandidateRejectPayload = {
  reason: string
}

export type CandidateBatchApproveItem = {
  candidate_id: string
  updates?: CandidateApprovePayload
}

export type CandidateBatchApproveRequest = {
  candidates: CandidateBatchApproveItem[]
}

export type CandidateBatchApproveResult = {
  candidate_id: string
  success: boolean
  detail?: string
  candidate?: VenueCandidateRecord
}

type DiscoveryPayload = {
  location: string
  similar_artists?: string[]
  genres?: string[]
  limit?: number
}

export async function fetchVenueCandidates(
  params?: FetchVenueCandidatesParams,
): Promise<VenueCandidateRecord[]> {
  const fetchStatus = params?.status ?? "pending"
  const safeLimit = Math.min(Math.max(params?.limit ?? 40, 1), 200)
  const query = new URLSearchParams()
  if (fetchStatus && fetchStatus !== "all") {
    query.set("status", fetchStatus)
  }
  query.set("limit", safeLimit.toString())
  const queryString = query.toString()
  const url = queryString ? `${BASE_PATH}/candidates?${queryString}` : `${BASE_PATH}/candidates`
  const payload = await request<VenueCandidateRecord[] | { items: VenueCandidateRecord[] }>(url)
  if (Array.isArray(payload)) {
    return payload
  }
  if (payload && Array.isArray((payload as { items: VenueCandidateRecord[] }).items)) {
    return (payload as { items: VenueCandidateRecord[] }).items
  }
  return []
}

export type UploadVenueDocumentsOptions = {
  tags?: string;
  owner?: string;
};

export async function uploadVenueDocuments(
  files: File[],
  options?: UploadVenueDocumentsOptions,
): Promise<VenueCandidateRecord[]> {
  const formData = new FormData()
  files.forEach((file) => formData.append("files", file))
  if (options?.tags) formData.append("tags", options.tags)
  if (options?.owner) formData.append("owner", options.owner)
  return request<VenueCandidateRecord[]>(`${BASE_PATH}/upload`, {
    method: "POST",
    body: formData,
  })
}

export async function discoverVenueCandidates(payload: DiscoveryPayload): Promise<VenueCandidateRecord[]> {
  return request<VenueCandidateRecord[]>(`${BASE_PATH}/discover`, {
    method: "POST",
    body: payload,
  })
}

export async function approveVenueCandidate(
  id: string,
  payload: CandidateApprovePayload,
): Promise<VenueCandidateRecord> {
  return request<VenueCandidateRecord>(`${BASE_PATH}/candidates/${id}/approve`, {
    method: "POST",
    body: payload,
  })
}

export async function runContactSearchForCandidate(
  id: string,
): Promise<Record<string, any>> {
  return request<Record<string, any>>(`${BASE_PATH}/candidates/${id}/contact-search`, {
    method: "POST",
  })
}

export async function rejectVenueCandidate(
  id: string,
  payload: CandidateRejectPayload,
): Promise<VenueCandidateRecord> {
  return request<VenueCandidateRecord>(`${BASE_PATH}/candidates/${id}/reject`, {
    method: "POST",
    body: payload,
  })
}

export type PasteTextPayload = {
  text: string
  filename?: string
  tags?: string
  owner?: string
}

export async function pasteVenueText(payload: PasteTextPayload): Promise<VenueCandidateRecord[]> {
  return request<VenueCandidateRecord[]>(`${BASE_PATH}/paste`, {
    method: "POST",
    body: payload,
  })
}

export async function batchApproveVenueCandidates(
  payload: CandidateBatchApproveRequest,
): Promise<CandidateBatchApproveResult[]> {
  return request<CandidateBatchApproveResult[]>(`${BASE_PATH}/candidates/batch/approve`, {
    method: "POST",
    body: payload,
    timeoutMs: 180_000,
  })
}
